import { useDraggable } from '@dnd-kit/core';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import {
  ArrowUturnLeftIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import { ExistingSystemComponentType } from 'data/types';
import {
  unplaceElectricalPanel,
  unplaceExistingSystemComponent
} from 'features/structure';
import { setActiveTab } from 'features/ui';
import {
  getAllElectricalPanels,
  getExistingEquipmentGridLabel,
  getExistingEquipmentTooltip,
  getExistingEquipmentTypeLabel,
  hasPlaceableExistingEquipment,
  isExistingCondenserComponent,
  isExistingEquipmentComponentPlaced
} from 'utils/existing-equipment';

type PlacementRowColor = 'purple' | 'orange' | 'yellow';

const ROW_COLOR_CLASSES: Record<PlacementRowColor, string> = {
  purple: 'bg-purple-100 border-purple-300',
  orange: 'bg-orange-100 border-orange-300',
  yellow: 'bg-yellow-100 border-yellow-300'
};

type PlacementDragData = {
  id: string;
  type: 'place-existing-equipment' | 'place-electrical-panel';
  itemId: string;
  label: string;
  gridObjectType?: 'existing-condenser' | 'existing-furnace';
};

// Draggable row for an UNPLACED item — drag onto the 2D grid to place it on
// the currently-viewed level (colors match the grid icons).
function PlacementDraggableRow({
  dragData,
  color,
  label,
  description,
  tooltip
}: {
  readonly dragData: PlacementDragData;
  readonly color: PlacementRowColor;
  readonly label: string;
  readonly description?: string;
  readonly tooltip?: string;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragData.id,
    data: dragData
  });

  return (
    <button
      type='button'
      ref={setNodeRef}
      style={{ opacity: isDragging ? 0.4 : 1, touchAction: 'none' }}
      className={clsx(
        'relative flex min-h-[40px] items-center gap-1 rounded-md border-2 py-1 pr-5 text-sm font-semibold',
        isDragging ? 'cursor-grabbing' : 'cursor-grab',
        ROW_COLOR_CLASSES[color]
      )}
      {...attributes}
      {...listeners}
    >
      <div className='flex shrink-0'>
        <EllipsisVerticalIcon className='h-4 w-4 translate-x-3' />
        <EllipsisVerticalIcon className='h-4 w-4 -translate-x-2' />
      </div>
      <div className='flex flex-col text-left'>
        <span>{label}</span>
        {description && (
          <span className='text-xs font-normal text-gray-600'>
            {description}
          </span>
        )}
      </div>
      {tooltip && <Tooltip text={tooltip} />}
    </button>
  );
}

// Greyed row for an already-placed item: shows its level and offers the
// un-place affordance (the icon returns to the draggable list above).
function PlacedRow({
  label,
  levelLabel,
  tooltip,
  onUnplace
}: {
  readonly label: string;
  readonly levelLabel: string;
  readonly tooltip?: string;
  readonly onUnplace: () => void;
}) {
  return (
    <div className='relative flex min-h-[40px] items-center gap-2 rounded-md border-2 border-gray-200 bg-gray-50 py-1 pl-3 pr-2 text-sm font-semibold text-gray-400'>
      <div className='flex min-w-0 flex-col text-left'>
        <span className='truncate'>{label}</span>
        <span className='text-xs font-normal'>Placed on {levelLabel}</span>
      </div>
      <button
        type='button'
        className='relative ml-auto shrink-0 cursor-pointer text-gray-400 hover:text-gray-700'
        onClick={onUnplace}
        aria-label={`Remove ${label} from the floor plan`}
      >
        <ArrowUturnLeftIcon className='h-5 w-5' />
        <Tooltip text='Remove from floor plan' />
      </button>
      {tooltip && <Tooltip text={tooltip} />}
    </div>
  );
}

// Placement-only step list (AHA-6681 batch 2): everything here was ENTERED
// on the Existing Equipment page — this list only places it. No add/edit
// affordances for equipment; edits happen back on that page.
export default function EquipmentPlacementList() {
  const dispatch = useAppDispatch();
  const { existingSystems, structure } = useAppSelector(
    (state) => state.structure
  );

  const panels = getAllElectricalPanels(structure);
  const systemsWithComponents = existingSystems.filter(
    (system) => system.components.length > 0
  );
  const hasAnyItems = hasPlaceableExistingEquipment(existingSystems, structure);

  const levelLabelFor = (levelUniqueId: string | undefined): string =>
    structure.find((level) => level.uniqueId === levelUniqueId)?.label ??
    'this floor';

  const renderComponentRow = (component: ExistingSystemComponentType) => {
    const label = getExistingEquipmentTypeLabel(component);
    const tooltip = getExistingEquipmentTooltip(component);

    if (isExistingEquipmentComponentPlaced(component)) {
      return (
        <PlacedRow
          key={component.uniqueId}
          label={label}
          levelLabel={levelLabelFor(component.levelUniqueId)}
          tooltip={tooltip}
          onUnplace={() =>
            dispatch(
              unplaceExistingSystemComponent({ uniqueId: component.uniqueId })
            )
          }
        />
      );
    }

    const isCondenser = isExistingCondenserComponent(component);
    return (
      <PlacementDraggableRow
        key={component.uniqueId}
        dragData={{
          id: `place-equipment-${component.uniqueId}`,
          type: 'place-existing-equipment',
          itemId: component.uniqueId,
          label: getExistingEquipmentGridLabel(component),
          gridObjectType: isCondenser
            ? 'existing-condenser'
            : 'existing-furnace'
        }}
        color={isCondenser ? 'purple' : 'orange'}
        label={label}
        tooltip={tooltip}
      />
    );
  };

  return (
    <div className='flex flex-col gap-4' id='equipment-placement-list'>
      {!hasAnyItems && (
        <div className='flex flex-col items-center gap-2 py-4 text-center'>
          <WrenchScrewdriverIcon className='h-10 w-10 text-gray-300' />
          <span className='font-semibold text-gray-700'>
            No existing equipment entered
          </span>
          <span className='text-sm text-gray-500'>
            Add equipment and electrical panels on the Existing Equipment page,
            then come back here to place them.
          </span>
        </div>
      )}
      {systemsWithComponents.length > 0 && (
        <div className='flex flex-col gap-2'>
          <span className='text-lg font-semibold'>Equipment</span>
          {systemsWithComponents.map((system) => (
            <div key={system.uniqueId} className='flex flex-col gap-2'>
              {/* A heading only disambiguates multiple systems — the
                  existing-equipment page convention. */}
              {systemsWithComponents.length > 1 && (
                <span className='text-sm font-semibold text-gray-700'>
                  {system.name}
                </span>
              )}
              {system.components.map(renderComponentRow)}
            </div>
          ))}
        </div>
      )}
      {panels.length > 0 && (
        <div className='flex flex-col gap-2'>
          <span className='text-lg font-semibold'>Electrical Panels</span>
          {panels.map(({ panel, levelLabel }) =>
            panel.position ? (
              <PlacedRow
                key={panel.uniqueId}
                label='Electrical Panel'
                levelLabel={levelLabel}
                onUnplace={() =>
                  dispatch(unplaceElectricalPanel({ uniqueId: panel.uniqueId }))
                }
              />
            ) : (
              <PlacementDraggableRow
                key={panel.uniqueId}
                dragData={{
                  id: `place-panel-${panel.uniqueId}`,
                  type: 'place-electrical-panel',
                  itemId: panel.uniqueId,
                  label: 'EP'
                }}
                color='yellow'
                label='Electrical Panel'
                description={
                  panel.amperage !== undefined
                    ? `${panel.amperage} A`
                    : undefined
                }
              />
            )
          )}
        </div>
      )}
      {hasAnyItems && (
        <span className='text-sm text-gray-600'>
          Drag an item onto the floor plan to place it on the level you are
          viewing. Drag a placed icon back here to remove it.
        </span>
      )}
      <button
        type='button'
        className='self-start text-sm font-medium text-blue-600 hover:text-blue-800'
        onClick={() => dispatch(setActiveTab('existingEquipment'))}
      >
        Add or edit equipment on the Existing Equipment page
      </button>
    </div>
  );
}
